import React from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  Button,
  Box,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { WordingGuardSuggestionChip } from "./WordingGuardSuggestionChip";

interface Props {
  desc: {
    language_code: string;
    actions: string[];
  };
  isReader: boolean;
  handleDescriptionsChange: (actions: string[], language_code: string) => void;
  getSuggestionsForAction?: (actionText: string, index: number) => Array<{ source: string; target: string; matches: number }>;
  onAcceptSuggestion?: (index: number, source: string, target: string) => void;
  onIgnoreSuggestion?: (index: number, source: string, target: string) => void;
  suggestedTemplates?: string[];
}

const ActionStepsInput: React.FC<Props> = ({
  desc,
  isReader,
  handleDescriptionsChange,
  getSuggestionsForAction,
  onAcceptSuggestion,
  onIgnoreSuggestion,
  suggestedTemplates = [],
}) => {
  const theme = useTheme();
  const languageKey = (desc.language_code || "lang").toLowerCase().replace(/[^a-z0-9_-]/g, "_");
  const [localActions, setLocalActions] = React.useState<string[]>(desc.actions ?? []);
  const commitTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const incoming = desc.actions ?? [];
    const sameLength = incoming.length === localActions.length;
    const sameValues = sameLength && incoming.every((v, i) => v === localActions[i]);
    if (!sameValues) {
      setLocalActions(incoming);
    }
  }, [desc.actions]);

  const commitActionsDebounced = React.useCallback((actions: string[]) => {
    if (commitTimeoutRef.current) {
      window.clearTimeout(commitTimeoutRef.current);
    }
    commitTimeoutRef.current = window.setTimeout(() => {
      handleDescriptionsChange(actions, desc.language_code);
      commitTimeoutRef.current = null;
    }, 180);
  }, [desc.language_code, handleDescriptionsChange]);

  React.useEffect(() => {
    return () => {
      if (commitTimeoutRef.current) {
        window.clearTimeout(commitTimeoutRef.current);
      }
    };
  }, []);

  const handleStepChange = (index: number, value: string) => {
    const updated = [...localActions];
    updated[index] = value;
    setLocalActions(updated);
    commitActionsDebounced(updated);
  };

  const addStep = () => {
    const updated = [...localActions, ""];
    setLocalActions(updated);
    handleDescriptionsChange(updated, desc.language_code);
  };

  const addSuggestedStep = (text: string) => {
    const nextText = String(text || "").trim();
    if (!nextText) return;
    const firstEmptyIndex = localActions.findIndex((entry) => String(entry || "").trim() === "");
    let updated: string[];
    if (firstEmptyIndex >= 0) {
      updated = [...localActions];
      updated[firstEmptyIndex] = nextText;
    } else {
      updated = [...localActions, nextText];
    }
    setLocalActions(updated);
    handleDescriptionsChange(updated, desc.language_code);
  };

  const removeStep = (index: number) => {
    const updated = localActions.filter((_, i) => i !== index);
    setLocalActions(updated);
    handleDescriptionsChange(updated, desc.language_code);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(localActions);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    setLocalActions(items);
    handleDescriptionsChange(items, desc.language_code);
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h6">
          {`Actions (${desc.language_code})`}
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Drag icon to change order
        </Typography>
      </Grid>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`actionSteps-${languageKey}`}>
          {(providedDroppable, snapshotDroppable) => (
            <Grid size={{ xs: 12 }}
              ref={providedDroppable.innerRef}
              {...providedDroppable.droppableProps}
              role="list"
              sx={{
                backgroundColor: snapshotDroppable.isDraggingOver
                  ? theme.palette.action.hover
                  : "transparent",
                borderRadius: 1,
                p: snapshotDroppable.isDraggingOver ? 1 : 0,
                transition: "background-color 120ms ease"
              }}
            >
              {localActions.map((step, index) => (
                <Draggable
                  key={`action-${languageKey}-${index}`}
                  draggableId={`action-${languageKey}-${index}`}
                  index={index}
                  isDragDisabled={isReader}
                >
                  {(providedDraggable, snapshotDraggable) => (
                    <Box
                      role="listitem"
                      ref={providedDraggable.innerRef}
                      {...providedDraggable.draggableProps}
                      sx={{
                        background: snapshotDraggable.isDragging
                          ? theme.palette.action.hover
                          : "transparent",
                        borderRadius: 1,
                        mb: 1,
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        boxShadow: snapshotDraggable.isDragging ? 3 : 0,
                        transition:
                          "transform 120ms ease, box-shadow 120ms ease",
                        transform: snapshotDraggable.isDragging
                          ? "scale(1.01)"
                          : "none"
                      }}
                    >
                      {/* Drag Handle Icon */}
                      {!isReader && (
                        <Tooltip title="Drag to reorder" arrow>
                          <IconButton
                            {...providedDraggable.dragHandleProps}
                            size="small"
                            sx={{
                              cursor: snapshotDraggable.isDragging
                                ? "grabbing"
                                : "grab",
                              color: theme.palette.text.secondary,
                              "&:hover": { color: theme.palette.text.primary }
                            }}
                            aria-label="Reorder step"
                          >
                            <DragIndicatorIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Index */}
                      <Typography
                        variant="body2"
                        sx={{ width: 24, color: "text.secondary" }}
                      >
                        {index + 1}.
                      </Typography>

                      {/* Text Input */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <TextField
                            fullWidth
                            value={step}
                            onChange={(e) =>
                              handleStepChange(index, e.target.value)
                            }
                            slotProps={{
                              input: { readOnly: isReader },
                              inputLabel: { shrink: true }
                            }}
                            required
                          />
                          {/* Remove Button */}
                          {!isReader && (
                            <Button
                              onClick={() => removeStep(index)}
                              variant="contained"
                              color="error"
                              size="small"
                              startIcon={<DeleteIcon />}
                              sx={{ whiteSpace: "nowrap", pl: 2, pr: 2, flexShrink: 0 }}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                        {!isReader && getSuggestionsForAction && (
                          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                            {getSuggestionsForAction(step, index).map((s) => (
                              <WordingGuardSuggestionChip
                                key={`action-suggestion-${languageKey}-${index}-${s.source}-${s.target}`}
                                label={`${s.source} -> ${s.target} (${s.matches})`}
                                onAccept={() => onAcceptSuggestion?.(index, s.source, s.target)}
                                onDismiss={() => onIgnoreSuggestion?.(index, s.source, s.target)}
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>
                    </Box>
                  )}
                </Draggable>
              ))}

              {providedDroppable.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>

      {!isReader && (
        <Grid size={{ xs: 12 }}>
          {suggestedTemplates.length > 0 && (
            <Box sx={{ mb: 1.25 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
                Suggested actions
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {suggestedTemplates.map((template, idx) => (
                  <Button
                    key={`action-template-${languageKey}-${idx}`}
                    size="small"
                    variant="outlined"
                    onClick={() => addSuggestedStep(template)}
                  >
                    {template}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
          <Button
            onClick={addStep}
            variant="contained"
            color="success"
            size="small"
            sx={{pl:1, pr:1}}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default ActionStepsInput;